import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button.jsx";
import ContactForm from "@/components/ContactForm.jsx";
import ThemeToggle from "@/components/ThemeToggle.jsx";
import SEO from "@/components/SEO.jsx";
import { CheckCircle, AlertCircle } from "lucide-react";
import useAsset from "@/hooks/useAsset";

// Tailtations Landing Page - Main marketing site
const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, resolvedTheme } = useTheme();
  const [showContactForm, setShowContactForm] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  // Cache-busted asset URLs
  const logoWhite = useAsset('/tailtations-white.svg');
  const logoDark = useAsset('/tailtations.svg');
  const lobsterDrawing = useAsset('/lobster-drawing.png');
  const palmTrees = useAsset('/palm-trees.png');
  const truckCenter = useAsset('/truck-center.png');
  const lobsterPlate = useAsset('/lobster-plate.png');
  const videoSrc = useAsset('/Tailtations_film_720p.mp4');
  const estImage = useAsset('/est.png');
  const mobileLobsterPlate = useAsset('/mobile-lobster-plate.png');
  const caviarImage = useAsset('/caviar.png');
  const sloganImage = useAsset('/slogan.png');
  const socialImage = useAsset('/social.png');
  const repeatBg = useAsset('/repeat-bg.png');
  const wholesalePack = useAsset('/images/wholesale.jpg');

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Scroll to top when sidebar opens
  useEffect(() => {
    if (isMobileSidebarOpen) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isMobileSidebarOpen]);

  // Lock/unlock page scrolling when sidebar opens/closes
  useEffect(() => {
    if (isMobileSidebarOpen) {
      // Lock scrolling
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.width = "100%";
    } else {
      // Restore scrolling
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [isMobileSidebarOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/.netlify/functions/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus("success");
        setTimeout(() => {
          setSubmitStatus(null);
          setFormData({
            name: "",
            email: "",
            subject: "",
            message: "",
          });
        }, 3000);
      } else {
        setSubmitStatus("error");
        console.error("Failed to send email:", result.error);
      }
    } catch (error) {
      setSubmitStatus("error");
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCateringClick = (e) => {
    e.preventDefault();
    setIsMobileSidebarOpen(false);
    navigate('/catering');
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    setFormData((prev) => ({ ...prev, subject: "General Inquiry" }));
    setIsMobileSidebarOpen(false);

    // Wait for sidebar to close before scrolling
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        document
          .getElementById("contact")
          .scrollIntoView({ behavior: "smooth" });
      }, 100);
    }, 300); // Match sidebar transition duration
  };

  const handleAboutClick = (e) => {
    e.preventDefault();
    setIsMobileSidebarOpen(false);

    // Wait for sidebar to close before scrolling
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        document.getElementById("about").scrollIntoView({ behavior: "smooth" });
      }, 100);
    }, 300); // Match sidebar transition duration
  };

  return (
    <div
      className="min-h-screen bg-background relative"
      style={{ scrollBehavior: "smooth" }}
    >
      <SEO
        title="Tailtations® - Gourmet Deep Fried Lobster Tails On Demand"
        description="Experience the ultimate luxury with Tailtations. We bring our gourmet, deep-fried lobster tails to your events with our full-service food truck."
      />
      {/* Grid Background */}
      <div
        className="fixed inset-0 opacity-20 pointer-events-none z-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(138, 49, 52, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(138, 49, 52, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      ></div>
      {/* Header */}
      <header
        className="bg-background py-8 px-8 border-b-2 relative z-10"
        style={{ borderBottomColor: "#8a3134" }}
      >
        <div className="container mx-auto flex items-center max-w-7xl">
          {/* Desktop Navigation - Left (equal width to right for centering) */}
          <div className="hidden md:flex items-center gap-2 w-[220px] shrink-0">
            <button
              onClick={handleCateringClick}
              className="px-3 py-1.5 text-sm font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors whitespace-nowrap"
            >
              Event Catering
            </button>
            <button
              onClick={() => document.getElementById('wholesale')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-3 py-1.5 text-sm font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors whitespace-nowrap"
            >
              Wholesale
            </button>
          </div>

          {/* Logo - Center */}
          <div className="flex-1 flex justify-center">
            <img
              src={resolvedTheme === 'dark' ? logoWhite : logoDark}
              alt="Tailtations Fried Lobster Brand"
              className="w-[250px] h-auto"
            />
          </div>

          {/* Desktop Navigation - Right (2x2 grid + theme toggle) */}
          <div className="hidden md:flex items-center gap-2 shrink-0 justify-end">
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={() => navigate('/taste')}
                className="w-full text-center px-3 py-1.5 text-sm font-semibold font-heading bg-[#8B3A3A] !text-white rounded-md hover:bg-[#6B2A2A] transition-colors whitespace-nowrap"
              >
                Order Online
              </button>
              <button
                onClick={() => navigate('/donate')}
                className="w-full text-center px-3 py-1.5 text-sm font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors whitespace-nowrap"
              >
                Donate
              </button>
              <button
                onClick={handleAboutClick}
                className="w-full text-center px-3 py-1.5 text-sm font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors whitespace-nowrap"
              >
                About
              </button>
              <button
                onClick={handleContactClick}
                className="w-full text-center px-3 py-1.5 text-sm font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors whitespace-nowrap"
              >
                Contact
              </button>
            </div>
            <ThemeToggle />
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="md:hidden flex items-center">
            {!isMobileSidebarOpen && (
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="flex items-center justify-center w-10 h-10 border border-border text-foreground rounded-md hover:bg-muted transition-colors"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <div
        className={`fixed inset-0 z-50 md:hidden ${isMobileSidebarOpen ? "block" : "hidden"}`}
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black"
          style={{ opacity: 0.5 }}
          onClick={() => setIsMobileSidebarOpen(false)}
        ></div>

        {/* Sidebar */}
        <div
          className={`fixed top-0 right-0 h-full w-80 bg-background shadow-lg transform transition-transform duration-300 ease-in-out ${isMobileSidebarOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex flex-col h-full">
            {/* Close Button - Top Right */}
            <div className="flex justify-end p-2">
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                className="flex items-center justify-center w-10 h-10 border border-border text-foreground rounded-md hover:bg-muted transition-colors"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Logo in Sidebar */}
            <div
              className="flex justify-center pt-2 pb-4 border-b-2"
              style={{ borderBottomColor: "#8a3134" }}
            >
              <img
                src={resolvedTheme === 'dark' ? logoWhite : logoDark}
                alt="Tailtations Fried Lobster Brand"
                className="w-[200px] h-auto"
              />
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 px-8 py-8 relative">
              <div className="space-y-6">
                <button
                  onClick={() => {
                    setIsMobileSidebarOpen(false)
                    navigate('/taste')
                  }}
                  className="w-full px-6 py-3 text-xl font-semibold font-heading bg-[#8B3A3A] !text-white rounded-md hover:bg-[#6B2A2A] transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  Order Online
                </button>
                <button
                  onClick={handleCateringClick}
                  className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  Event Catering
                </button>
                <button
                  onClick={() => {
                    setIsMobileSidebarOpen(false)
                    setTimeout(() => {
                      document.getElementById('wholesale')?.scrollIntoView({ behavior: 'smooth' })
                    }, 300)
                  }}
                  className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  Wholesale
                </button>
                <button
                  onClick={() => {
                    setIsMobileSidebarOpen(false)
                    navigate('/donate')
                  }}
                  className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  Donate
                </button>
                <button
                  onClick={handleAboutClick}
                  className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  About
                </button>
                <button
                  onClick={handleContactClick}
                  className="w-full px-6 py-3 text-xl font-semibold font-heading border border-primary dark:border-gray-400 text-gray-900 dark:text-foreground rounded-md hover:bg-muted transition-colors"
                  style={{ lineHeight: '1' }}
                >
                  Contact
                </button>
                <div className="pt-4">
                  <ThemeToggle />
                </div>
              </div>

              {/* Lobster Drawing - Bottom Right */}
              <div className="absolute bottom-0 right-0">
                <img
                  src={lobsterDrawing}
                  alt="Lobster Drawing"
                  className="w-32 h-auto opacity-80"
                />
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Hero Heading Section */}
      <section className="border-b-2 border-[#8A3134] bg-background py-12">
        <div className="container mx-auto text-center px-6">
          <h1
            className="text-4xl font-bold leading-tight animate-fade-in font-heading"
            style={{
              color: "#962831",
            }}
          >
            Los Angeles' Original Fried Lobster Brand
          </h1>
        </div>
      </section>

      {/* New Hero Section */}
      <section className="border-b-2 border-[#8A3134] bg-background">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full mx-auto lg:pt-8 md:pt-0 pt-6 lg:px-0 px-10 items-center">
          {/* Left Image */}
          <div className="lg:flex justify-start hidden">
            <img
              src={palmTrees}
              alt="Palm Trees"
              className="h-80 object-contain"
            />
          </div>

          {/* Center Image */}
          <div className="flex justify-center">
            <a
              href="https://secret.tailtations.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
            >
              <img
                src={truckCenter}
                alt="Tailtations Food Truck"
                className="h-auto lg:!w-[577px] lg:max-w-none animate-slide-in-right hover:opacity-90 transition-opacity"
                style={{
                  animation: 'slideInRight 1.2s ease-out',
                  transform: 'translateX(100vw)',
                  animationFillMode: 'forwards'
                }}
              />
            </a>
          </div>

          {/* Right Image */}
          <div className="hidden md:flex justify-center lg:justify-end">
            <img
              src={lobsterPlate}
              alt="Lobster Bowl"
              className="h-80 lg:w-auto w-[200px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Mobile Event Catering CTA */}
      <section className="md:hidden border-b-2 border-[#8A3134] bg-background py-6">
        <div className="container mx-auto px-6 space-y-4">
          <button
            onClick={handleCateringClick}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 rounded-lg shadow-lg transition-all duration-150 text-xl font-heading"
            style={{
              maxHeight: '150px',
              backgroundColor: '#8a3134',
              color: 'white'
            }}
          >
            Book Event Catering
          </button>

          {/* ============================================================================
              THANKSGIVING 2025 CAMPAIGN - HIDDEN (Campaign Concluded)
              Uncomment this section to re-enable for future Thanksgiving campaigns
              ============================================================================ */}
          {/* <button
            onClick={() => navigate('/donate')}
            className="relative w-full bg-white border-2 border-dashed border-[#8A3134] rounded-lg p-4 shadow-lg transition-all duration-150 hover:shadow-xl hover:border-solid overflow-hidden"
          >
            <div className="absolute top-3 -left-8 bg-[#8A3134] text-white text-xs font-bold py-1 px-8 transform -rotate-45 shadow-md">
              DONATE
            </div>
            <div className="text-center">
              <div className="text-2xl mb-2">🦞 🦃</div>
              <h3 className="text-[#8A3134] font-bold text-lg font-heading">Thanksgiving Giving Program</h3>
              <p className="text-gray-600 text-sm mt-1">Help us feed Skid Row this Thanksgiving</p>
              <p className="text-[#8A3134] font-semibold text-xs mt-2">November 27, 2025</p>
            </div>
          </button> */}
        </div>
      </section>

      {/* ============================================================================
          THANKSGIVING 2025 DESKTOP CTA - HIDDEN (Campaign Concluded)
          Uncomment this section to re-enable for future Thanksgiving campaigns
          This includes the turkey repeating background pattern
          ============================================================================ */}
      {/* <section 
        className="hidden md:block border-b-2 border-[#8A3134] py-8 relative"
        style={{
          backgroundImage: 'url(/turktations-repeating-bg.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: '150px',
          backgroundColor: 'white'
        }}
      >
        <div className="absolute inset-0 bg-white/60 pointer-events-none" />
        <div className="container mx-auto px-6 relative z-10">
          <button
            onClick={() => navigate('/donate')}
            className="relative mx-auto block max-w-xl bg-white border-2 border-dashed border-[#8A3134] rounded-lg p-6 shadow-lg transition-all duration-150 hover:shadow-xl hover:border-solid overflow-hidden"
          >
            <div className="absolute top-4 -left-8 bg-[#8A3134] text-white text-xs font-bold py-1 px-8 transform -rotate-45 shadow-md">
              DONATE
            </div>
            <div className="text-center">
              <div className="text-3xl mb-3">🦞 🦃</div>
              <h3 className="text-[#8A3134] font-bold text-xl font-heading">Thanksgiving Giving Program</h3>
              <p className="text-gray-600 text-base mt-2">Help us feed Skid Row this Thanksgiving</p>
              <p className="text-[#8A3134] font-semibold text-sm mt-3">November 27, 2025</p>
            </div>
          </button>
        </div>
      </section> */}

      {/* Video Background Section */}
      <section className="relative w-full aspect-video overflow-hidden border-b-2 border-[#8A3134] bg-white">
        {/* Loading Spinner */}
        {isVideoLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
            <img
              src={lobsterDrawing}
              alt="Loading"
              className="w-[150px] h-[150px] animate-spin-slow"
              style={{ animationDirection: 'reverse' }}
            />
            <p className="mt-4 text-[#962831] font-light text-lg">Loading video...</p>
          </div>
        )}

        {/* Video Background */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => setIsVideoLoading(false)}
          onCanPlayThrough={() => setIsVideoLoading(false)}
        >
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </section>

      {/* New Brand Story Section */}
      <section id="about">
        <div className="flex flex-col-reverse lg:flex-row justify-between w-full lg:px-0 sm:px-10 px-4">
          <div className="flex flex-row lg:flex-col lg:justify-center sm:justify-evenly justify-center md:gap-30 gap-4 items-center lg:w-[30%] w-full lg:border-0 border-t-2 border-x-2 border-[#8A3134] lg:p-4 p-8">
            <div className="">
              <img
                src={estImage}
                alt="EST 2025"
                className="h-40 w-auto sm:block hidden"
              />
            </div>
            <div className="">
              <img
                src={mobileLobsterPlate}
                alt="Lobster Drawing"
                className="w-60 h-auto md:hidden"
              />
              <img
                src={lobsterDrawing}
                alt="Lobster Drawing"
                className="lg:w-80 md:w-40 w-30 h-auto hidden md:block"
              />
            </div>
          </div>
          <div className="border-x-2 border-[#8A3134] lg:w-[40%] w-full md:py-16 py-4 md:px-12 px-4">
            <p
              className="text-foreground text-base leading-relaxed mb-6"
              style={{ fontStyle: "italic" }}
            >
              Tailtations wasn't born from a business plan — it started in my
              kitchen, out of love for sharing good food with good people. For
              over seven years, I've been hand-preparing lobster tails —
              butterflying, breading, and frying them — not for profit, but for
              the joy of bringing something special to friends, family, and
              colleagues. From holiday dinners to casual gatherings, showing up
              with a tray of deep-fried lobster tails became my little party
              tradition. And to my surprise, it became something folks looked
              forward to, talked about, and remembered long after the plates
              were cleared.
            </p>
            <p
              className="text-foreground text-base leading-relaxed mb-6"
              style={{ fontStyle: "italic" }}
            >
              Over time, people started nudging me: "You really should do
              something with this." I didn't set out to start a food brand —
              I've spent most of my life building businesses, not recipes. But
              with enough encouragement, I listened. And so, Tailtations was
              born — not out of ambition, but out of gratitude.
            </p>
            <p
              className="text-foreground text-base leading-relaxed mb-6"
              style={{ fontStyle: "italic" }}
            >
              The recipe is inspired by the technique behind a
              two-Michelin-starred fried poultry dish, reimagined for cold water
              lobster tails. The result is tender, juicy, and free of the
              overpowering aftertaste some folks associate with seafood. It took
              years of quiet tweaking, lots of taste tests, and more than a few
              honest friends to get it right.
            </p>
            <p
              className="text-foreground text-base leading-relaxed mb-6"
              style={{ fontStyle: "italic" }}
            >
              Tailtations is still just getting started. But it's built on
              something real — a love for craft, a respect for good ingredients,
              and a deep belief that sometimes, the best ideas come from sharing
              a meal.
            </p>
            <p className="text-right text-base text-muted-foreground italic mt-8">
              — Chef Austin Hurst
            </p>
          </div>
          <div className="hidden lg:flex justify-center items-center w-[30%]">
            <img src={caviarImage} alt="Caviar Bowl" className="w-72 h-auto" />
          </div>
        </div>
      </section>

      {/* WHOLESALE / FOODSERVICE — drives to /wholesale funnel */}
      <section
        id="wholesale"
        className="relative border-t-2 overflow-hidden scroll-mt-20"
        style={{ borderTopColor: "#8a3134", backgroundColor: "#0d0d0d" }}
      >
        {/* Subtle brand texture */}
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            backgroundImage: `url('${repeatBg}')`,
            backgroundRepeat: "repeat",
            backgroundSize: "200px",
            opacity: 0.05,
          }}
        />

        <div className="relative z-10 container mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left — copy */}
            <div>
              <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4 !text-[#C13340]">
                For Restaurants · Hotels · Distributors
              </p>

              <h2 className="text-[28px] sm:text-3xl lg:text-4xl font-bold leading-[1.1] font-heading mb-4 !text-white">
                Place the Tailtations<span className="text-[0.55em] !text-white" style={{ verticalAlign: "0.6em" }}>®</span> Signature Deep Fried Lobster Recipe on Your Menu.
              </h2>
              <p className="text-lg sm:text-xl font-semibold font-heading mb-5 !text-white/55">
                No Skilled Chef Required.
              </p>

              <p className="text-base !text-white/65 font-body leading-relaxed mb-7 max-w-xl">
                Frozen-to-fryer in 5 minutes. Plate restaurant-quality lobster
                consistently — without prep or waste. Now available wholesale for
                foodservice operators and distribution.
              </p>

              {/* Mini-stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-7">
                {[
                  { stat: "5 Min", label: "Frozen → fryer at 325°F. No thawing." },
                  { stat: "Zero Prep", label: "Hand-cracked & pre-cooked in our kitchen." },
                  { stat: "8 Months", label: "Blast-chilled −68°F. Stocked SKU, no shrink." },
                  { stat: "$12.50-$16", label: "Per tail. Plates at a $25–$45 menu price.", note: "*Verified wholesale buyers meeting MOQ" },
                ].map((item) => (
                  <div
                    key={item.stat}
                    className="rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3"
                  >
                    <div className="text-lg lg:text-xl font-bold font-heading !text-[#C13340] mb-1 whitespace-nowrap">
                      {item.stat}
                    </div>
                    <div className="text-[11px] leading-snug !text-white/50">{item.label}</div>
                    {item.note && (
                      <div className="mt-1.5 text-[10px] font-medium uppercase tracking-wide !text-white/35">
                        {item.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/wholesale")}
                  className="inline-flex items-center justify-center bg-[#962831] hover:bg-[#7a2029] !text-white font-bold py-3 px-6 rounded-lg text-base font-heading transition-all duration-300 hover:scale-[1.02] shadow-[0_0_24px_rgba(193,51,64,0.3)]"
                >
                  Explore Wholesale &rarr;
                </button>
                <a
                  href="mailto:contact@tailtations.com?subject=Wholesale%20%2F%20Distribution%20Inquiry&body=Tell%20us%20about%20your%20operation%20(restaurant%2C%20hotel%2C%20group%2C%20or%20distributor)%20and%20estimated%20volume%2C%20and%20we%27ll%20be%20in%20touch."
                  className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 !text-white font-bold py-3 px-6 rounded-lg text-base font-heading transition-colors"
                >
                  Talk to Distribution
                </a>
              </div>

              {/* Sample-kit + negotiable-MOQ note */}
              <p className="mt-4 text-sm leading-relaxed !text-white/55 max-w-xl">
                New to Tailtations? *Verified wholesale buyers can{" "}
                <a
                  href="mailto:contact@tailtations.com?subject=Request%20a%2030-Pack%20Sample%20Kit&body=We%27d%20like%20to%20request%20a%2030-pack%20sample%20kit.%20Here%27s%20our%20business%20(name%2C%20type%2C%20location)%20and%20resale%2Fbusiness%20details%20for%20verification%3A"
                  className="font-semibold underline !text-[#C13340] hover:!text-[#962831]"
                >
                  request a 30-pack sample kit
                </a>{" "}
                to taste the product first. Minimum order quantities are a starting
                point — we negotiate volume pricing and MOQs as your account grows.
              </p>
            </div>

            {/* Right — wholesale pack photo */}
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src={wholesalePack}
                alt="Tailtations 30-Pack Wholesale Kit branded packaging"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* NEW GET IN TOUCH */}
      <section id="contact" className="border-t-2 border-[#8A3134] py-10">
        <div className="flex flex-col lg:flex-row w-full max-w-[1440px] mx-auto px-6">
          <div className=" flex lg:flex-col md:flex-row flex-col lg:w-[30%] w-full justify-between lg:ml-[calc(5%-22px)]">
            <h3 className="text-2xl font-semibold leading-tight mb-12 font-heading text-primary">
              Get in Touch
            </h3>

            <div className="">
              <img
                src={sloganImage}
                alt="One and Only The Crunchy Savory Lobster Food Truck"
                className="w-80 h-auto"
              />
            </div>
          </div>
          <div className="lg:w-[30%] w-full  lg:pt-20 py-20 lg:block flex justify-center">
            <div
            // className="absolute"
            // style={{
            //   top: "20px",
            //   left: "50%",
            //   transform: "translateX(-50%)",
            // }}
            >
              <a
                href="https://www.instagram.com/tailtations"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src={socialImage}
                  alt="Social Media Icons"
                  className="w-64 h-auto cursor-pointer hover:opacity-80 transition-opacity"
                />
              </a>
            </div>

          </div>

          {/* Right Column - Contact Form */}
          <div className="flex-1 md:pl-8 px-4 md:px-0 flex justify-center md:justify-start pt-2 md:pt-2 lg:py-0 lg:items-center" style={{ minHeight: "500px" }}>
            <div className="w-full max-w-md md:max-w-none">
            {submitStatus === "success" ? (
              <div className="text-center py-8">
                <CheckCircle
                  className="h-12 w-12 mx-auto mb-4"
                  style={{ color: "#8a3134" }}
                />
                <h3
                  className="text-lg font-semibold mb-2"
                  style={{ color: "#8a3134" }}
                >
                  Thank You!
                </h3>
                <p className="text-muted-foreground">
                  We'll be in touch within 24 hours.
                </p>
              </div>
            ) : submitStatus === "error" ? (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-600 mb-2">
                  Failed to Submit
                </h3>
                <p className="text-muted-foreground mb-4">
                  There was an error submitting your message. Please try again.
                </p>
                <Button
                  onClick={() => setSubmitStatus(null)}
                  variant="outline"
                  style={{ borderColor: "#8a3134", color: "#8a3134" }}
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Name <span className="text-destructive ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email <span className="text-destructive ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Subject
                  </label>
                  <div className="relative">
                    <select
                      value={formData.subject}
                      onChange={(e) => handleChange("subject", e.target.value)}
                      className="w-full pl-4 pr-12 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent appearance-none"
                    >
                      <option value="">What's this about?</option>
                      <option value="Event Catering">Event Catering</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Partnership">Partnership</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Message <span className="text-destructive ml-1">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    className="w-full px-4 py-2 bg-transparent border border-border text-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                    placeholder="Your message..."
                    required
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
            </div>
          </div>
        </div>
      </section>

      {/* Red Divider Bar - Top */}
      <div className="h-0.5" style={{ backgroundColor: "#8a3134" }}></div>

      {/* Pattern Background Section */}
      <section
        className="bg-background relative"
        style={{
          backgroundImage: `url(${repeatBg})`,
          backgroundRepeat: "repeat",
          backgroundSize: "1440px",
          backgroundPosition: "center",
          opacity: 1,
          height: "228px",
        }}
      ></section>


      {/* Red Bar - Bottom */}
      <div style={{ backgroundColor: "#8a3134", height: "50px" }}></div>

      {/* Background Pattern */}
      <div
        className="fixed inset-0 opacity-5 pointer-events-none z-0"
        style={{
          backgroundImage: `url(${repeatBg})`,
          backgroundRepeat: "repeat",
          backgroundSize: "200px",
        }}
      ></div>

      {/* Contact Form Modal */}
      <ContactForm
        isOpen={showContactForm}
        onClose={() => setShowContactForm(false)}
      />
    </div>
  );
};

export default LandingPage;
